<template>
    <!-- classes used here are defined in tailwind.css, it is in the @layer components to correctly get overridden by utility layer classes -->
    <div class="progress-bar-container">
        <progress
            :id="progressId"
            :value="progressValue"
            :class="['progress-bar', sizeClass]"
            :style="color ? { '--progress-color': color } : undefined"
            :aria-label="ariaLabel"
            :aria-busy="isLoading"
            :max="max" />
        <p v-if="showLabel" class="body-small min-w-10 text-right">{{ progressLabel }}</p>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useId } from "vue";

type BaseProgressBarProps = {
    value: number;
    // whether to use the progress bar as a loading state
    isLoading?: boolean;
    // if isLoading is set, how long the loading animation should take ms
    loadingTime?: number;
    // toggles whether the percentage label is shown on the progress bar
    showLabel?: boolean;
    // use this aria-label prop to provide a more descriptive label for what this progress bar represents
    ariaLabel?: string;
    max?: number;
    size?: "xs" | "sm" | "md";
    // CSS color value for the filled portion; defaults to the --color-progress-fill token
    color?: string;
};
const { value, isLoading = false, loadingTime = 15000, showLabel = false, ariaLabel = "progress bar", max = 100, size = "sm", color } = defineProps<BaseProgressBarProps>();

const internalValue = ref(value);

let animationFrameId: number | null = null;
let startTime: number | null = null;

const animate = (timestamp: number) => {
    if (!startTime) {
        startTime = timestamp;
    }
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / loadingTime, 1);
    internalValue.value = value + progress * (max - value);

    if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
    }
};

const progressValue = computed(() => {
    if (!isLoading) {
        return value;
    }
    return internalValue.value;
});

onMounted(() => {
    if (isLoading) {
        animationFrameId = requestAnimationFrame(animate);
    }
});

onUnmounted(() => {
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
    }
});

const progressLabel = computed(() => {
    const percentage = Math.round((progressValue.value / max) * 100);
    return `${percentage}%`;
});

const sizeClass = computed(() => {
    switch (size) {
        case "xs":
            return "[--progress-height:0.25rem]";
        case "sm":
            return "[--progress-height:0.5rem]";
        default:
            return "[--progress-height:0.625rem]";
    }
});

const progressId = useId();
</script>
